/**
 * GID-202: what the pane shows while an answer is being worked out.
 *
 * Progress used to land in the transcript as permanent "system" messages, and
 * only `retry_triggered` was handled at all — `pi_edit_started` was dropped on
 * the floor. A one-off notice in the conversation is the wrong shape for
 * something that is only true while the request is in flight, and it left the
 * finished answer with no record of which route or model produced it.
 *
 * This module owns the wording and the ordering. It holds no DOM, so the
 * decisions it makes are testable on their own; taskpane.js renders whatever
 * it reports.
 */

/**
 * Keyed by the `route` values emitted in backend orchestrator.py. When a route
 * is added there without a matching entry here the fallback still renders a
 * readable label, so the pane degrades rather than printing a raw token.
 */
const ROUTE_PRESENTATION = Object.freeze({
  questions: {
    label: "Question",
    explanation: "Reading your plan to answer. Nothing is changed.",
  },
  edit_gantt: {
    label: "Edit",
    explanation: "Working out the changes to your schedule.",
  },
  generation: {
    label: "Generate",
    explanation: "Building a new schedule from your description.",
  },
  filtering: {
    label: "Filter",
    explanation: "Finding the matching tasks to mark in a field.",
  },
  compare: {
    label: "Compare",
    explanation: "Comparing two versions of the plan.",
  },
  calendar: {
    label: "Calendar",
    explanation: "Preparing calendar changes. Nothing changes in Project until you click Sync.",
  },
  update_inplace: {
    // "Edit", not "Update": the reader is being told what is happening to their
    // plan, and they call that editing. edit_gantt below is deliberately the
    // same label -- both are edits from the user's side, and the explanation
    // carries the difference between changing tasks in place and rebuilding.
    label: "Edit",
    explanation: "Editing tasks in place, keeping the rest of the plan as it is.",
  },
});

/**
 * Wording for events that arrive without a message of their own. The backend
 * no longer gates progress on a fixed list, so an event this map has never
 * heard of still renders — as long as it brought its own message.
 */
const EVENT_FALLBACK_TEXT = Object.freeze({
  retry_triggered: "The first attempt looked incomplete. Retrying.",
  pi_edit_started: "Editing and validating the schedule.",
  route_selected: "Choosing how to answer.",
  context_ready: "Read your plan.",
  working: "Working through your schedule.",
});

function normaliseRouteKey(route) {
  return String(route).trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function sentenceCase(token) {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/**
 * The label and one-line explanation for a route.
 *
 * @param {string|null|undefined} route
 * @returns {{label: string, explanation: string}|null} null when there is no
 *   route to describe — callers use that to mean "say nothing", never "unknown".
 */
export function describeRoute(route) {
  if (!route) return null;

  const key = normaliseRouteKey(route);
  if (!key) return null;

  const known = ROUTE_PRESENTATION[key];
  if (known) return { ...known };

  return { label: sentenceCase(key.replace(/_/g, " ")), explanation: "" };
}

/**
 * How long the request has been running, for the line the user is watching.
 *
 * Seconds alone up to a minute; minutes and padded seconds beyond, because a
 * generation runs for minutes and "132s" is harder to read at a glance.
 *
 * @param {number|null|undefined} ms
 * @returns {string}
 */
export function formatElapsed(ms) {
  const total = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  if (total < 60) return `${total}s`;

  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return `${minutes}m ${seconds}s`;
}

/**
 * One line of the live feed, built from a streamed progress event.
 *
 * Route and model are labelled ("Route: Question", not "Question"): these lines
 * render inside the message list, where a bare noun reads as chat content
 * rather than as status.
 *
 * @param {Object|null} metadata - the `metadata` of a stream chunk
 * @returns {{id: string, text: string, detail: string, note: string}|null}
 *   null when the chunk is not a progress event
 */
export function buildProgressEntry(metadata) {
  const event = metadata?.stream_event;
  if (!event) return null;

  const id = String(event);

  if (id === "route_selected") {
    const described = describeRoute(metadata.route);
    if (!described) return null;

    return {
      id,
      text: `Route: ${described.label}`,
      detail: "",
      note: described.explanation,
    };
  }

  const text = String(metadata.message || EVENT_FALLBACK_TEXT[id] || "").trim();
  if (!text) return null;

  return { id, text, detail: "", note: "" };
}

/**
 * The line left attached to the finished answer: which route handled it.
 *
 * The model is deliberately not shown. It is an internal detail the reader
 * cannot act on, and in a 460px pane it crowded out the thing they can.
 * It still travels in the response metadata for Langfuse.
 *
 * @param {{route?: string}|null} run
 * @returns {string|null} null when the route is unknown
 */
export function summariseRun(run) {
  const described = describeRoute(run?.route);
  return described?.label ? `Route: ${described.label}` : null;
}

/**
 * Collects the feed for a single request.
 *
 * Re-firing an event replaces its line rather than appending a second one: a
 * retry re-routes, and two `route_selected` lines would read as two routes
 * having been taken when only the latest is true.
 *
 * @returns {{add: Function, observeMetadata: Function, entries: Function,
 *   summary: Function, clear: Function}}
 */
export function createActivityLog() {
  let entries = [];
  let run = { route: "", model: "" };

  /**
   * Last non-empty value wins, per key. This mirrors _merge_trace_tag_metadata
   * on the backend: a trailing telemetry chunk carries metadata with no route,
   * and must not erase a route an earlier chunk established.
   */
  const remember = (metadata) => {
    if (!metadata) return;
    if (metadata.route) run = { ...run, route: metadata.route };
    if (metadata.model) run = { ...run, model: metadata.model };
  };

  return {
    /** Record a streamed progress event. Ignores non-progress chunks. */
    add(metadata) {
      const entry = buildProgressEntry(metadata);
      if (!entry) return null;

      remember(metadata);
      entries = [...entries.filter((existing) => existing.id !== entry.id), entry];
      return entry;
    },

    /** Pick up route/model from any chunk, including the final one. */
    observeMetadata(metadata) {
      remember(metadata);
    },

    entries() {
      return [...entries];
    },

    summary() {
      return summariseRun(run);
    },

    clear() {
      entries = [];
      run = { route: "", model: "" };
    },
  };
}
